import { FundRequest, FundRequestFilters } from '~/types/fundrequests';
import { apiUrl, authHeaders } from './auth';
import queryString from 'query-string';
import { SelectOption } from '~/types/globals';
import { FundRequestStatus } from '~/enums/fundRequest';
import { handleResponse } from '~/utils';

export const defaultFundRequestFilters: FundRequestFilters = {
    status: FundRequestStatus.OPEN,
    page: 1,
    coordinator: '',
    created_date_start: '',
    created_date_end: '',
    patient_id: '',
    vendor_id: null,
    fund_request_id: '',
    source: '',
    sort: 'desc',
    sort_by: 'updated_date',
};

export const fetchFundRequests = async (
    defaultFundRequestFilters: FundRequestFilters
) => {
    const filterParams = queryString.stringify(defaultFundRequestFilters, {
        skipNull: true,
        skipEmptyString: true,
        strict: false,
    });
    console.log('filterParams: ', filterParams);

    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests?${filterParams}`;
    console.log('Request URL: ', url);

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    return handleResponse(resp);
};

export const searchFundRequests = async (
    filters: FundRequestFilters
) => {
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
        strict: false,
    });

    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/search?${filterParams}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    return handleResponse(resp);
};

export const fetchFundRequestStatusCounts = async () => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/status-counts`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    return handleResponse(resp);
};

export const fetchFundRequestCategories = async (): Promise<SelectOption[]> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/fundrequests/categories';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchVendors = async (search = '') => {
    const params = new URLSearchParams({ search });
    const headers = authHeaders();
    const url = `${apiUrl}/admin/dropdown/vendors?${params.toString()}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching vendors');
    }
    const json = await resp.json();
    return json;
};

export const fetchPatients = async (search = '') => {
    const params = new URLSearchParams({ search });
    const headers = authHeaders();
    const url = `${apiUrl}/admin/dropdown/patients?${params.toString()}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching patients');
    }
    const json = await resp.json();
    return json;
};

export const deleteFundRequest = async (id: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}`;
    
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    
    if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Error deleting fund request');
    }
    
    return resp;
};

export const fetchFinancialCoordinators = async () => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/dropdown/financial-coordinators`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching financial coordinators');
    }
    const json = await resp.json();
    return json;
};

export const fetchStatusDurationReport = async (
    filters: { submitted_date_start: string; submitted_date_end: string; page: number }
) => {
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
        strict: false,
    });

    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/status-duration-report?${filterParams}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    return handleResponse(resp);
};

export const fetchFundRequestDetail = async (
    id: string
): Promise<FundRequest> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching fund request');
    }
    const json = await resp.json();
    return json;
};

export const fetchFundRequestAvailableBalance = async (
    ntaf_id: string
): Promise<any> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/available-balance/${ntaf_id}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    // if (!resp.ok) {
    //     throw new Error('Error fetching fund request');
    // }
    const json = await resp.json();
    return json;
};

export const refreshFundRequestAvailableBalance = async (
    ntaf_id: string
): Promise<any> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/refresh-balance/${ntaf_id}`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    
    if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Error refreshing balance');
    }
    
    const json = await resp.json();
    return json;
};

export const createDraft = async (data: any) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/fundrequests/create-draft';

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        throw new Error('Error creating draft');
    }
    const json = await resp.json();
    return json;
}

export const assignCampaignFinancialCoordinator = async (
    campaign_id: string,
    person_id: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaign_id}/assignFc`;

    const resp = await fetch(url, {
        method: 'put',
        headers,
        body: JSON.stringify({ person_id }),
    });
    if (!resp.ok) {
        throw new Error('Error assigning financial coordinator');
    }
    const json = await resp.json();
    return json;
};

export const assignFinancialCoordinator = async (
    fund_request_id: string,
    coordinator_id: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${fund_request_id}/assignFc`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ coordinator_id }),
    });
    if (!resp.ok) {
        throw new Error('Error assigning financial coordinator');
    }
    const json = await resp.json();
    return json;
};

export const addFundRequestNote = async (
    note: string,
    fundRequestId: number,
    isInternal: boolean = false
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${fundRequestId}/notes/`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ note: note, is_internal: isInternal }),
    });
    if (!resp.ok) {
        throw new Error('Error fetching fund request');
    }
    const json = await resp.json();
    return json;
};

export const markFundRequestNoteRead = async (
    noteId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/notes/${noteId}/mark-read`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error marking note as read');
    }
    const json = await resp.json();
    return json;
};

export const toggleFundRequestNoteInternal = async (noteId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/notes/${noteId}/toggle-internal`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error toggling note internal flag');
    }
    const json = await resp.json();
    return json;
};

export const updateFundRequest = async (id: string, data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}`;

    const resp = await fetch(url, {
        method: 'put',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const deleteFundRequestItem = async (
    id: number,
    itemId: number,
    itemType: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/expenseitems/${itemType.toLowerCase()}/${itemId}`;

    const resp = await fetch(url, {
        method: 'delete',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const addFundRequestItemFlag = async (data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/flag`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const resolveFundRequestItemFlag = async (data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/flag/resolve`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const clearFundRequestItemFlag = async (data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/flag/delete`;

    const resp = await fetch(url, {
        method: 'delete',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        throw new Error('Error clearing flag from expense item');
    }
    const json = await resp.json();
    return json;
};

export const setStatusNeedsAdditionalInfo = async (
    id: string,
    needs_info_note: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/needs-additional-info`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ needs_info_note }),
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const sendNeedsInfoReminder = async (id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/send-needs-info-reminder`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error sending reminder');
    }
    const json = await resp.json();
    return json;
};

export const suggestPartialDisbursement = async (
    id: string,
    message: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/suggest-partial-disbursement`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ message }),
    });
    if (!resp.ok) {
        throw new Error('Error suggesting partial disbursement');
    }
    const json = await resp.json();
    return json;
};

export const createPartialDisbursement = async (
    id: string,
    partialAmounts: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/partial-disbursement/create`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(partialAmounts),
    });
    if (!resp.ok) {
        throw new Error('Error creating partial disbursement');
    }
    const json = await resp.json();
    return json;
};

export const revertPartialDisbursement = async (id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/partial-disbursement/revert`;

    const resp = await fetch(url, {
        method: 'delete',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error reverting partial disbursement remainder');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignTeamMembers = async (campaignId: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/team-members?campaign_id=${campaignId}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching campaign team members');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignAvailableContacts = async (campaignId: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/available-contacts?campaign_id=${campaignId}`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching campaign available contacts');
    }
    const json = await resp.json();
    return json;
};

export const requestACHInfo = async (
    id: string,
    needs_ach_info_note: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/needs-ach-info`;
    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ needs_ach_info_note }),
    });
    if (!resp.ok) {
        throw new Error('Error updating fund request');
    }
    const json = await resp.json();
    return json;
};

export const setStatusApReview = async (id: string, priority: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/ap-review`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ priority }),
    });
    return handleResponse(resp);
};

export const addToQueue = async (id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/add-to-queue`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    return handleResponse(resp);
};

export const fetchQueue = async () => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/queue`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    return handleResponse(resp);
};

export const submitQueue = async (payment_type: string, scheduled_date?: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/queue/submit`;

    const body: any = { payment_type };
    if (scheduled_date) {
        body.scheduled_date = scheduled_date;
    }

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(body),
    });

    const json = await resp.json();

    // Handle different status codes from the new backend response
    if (resp.ok || resp.status === 207) {
        // Success (200) or Multi-Status (207) for partial success
        return json;
    } else {
        // Error case - still return the JSON for processing result, but throw error
        const error: any = new Error(json.error || 'Queue submission failed');
        error.response = {
            status: resp.status,
            data: json
        };
        throw error;
    }
};

// Test function for development - can be used for quick testing
export const submitQueueTest = async (payment_type: string, test_scenario: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/queue/submit`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({
            payment_type,
            test_scenario  // This triggers the backend test mode
        }),
    });

    const json = await resp.json();

    // Handle different status codes from the new backend response
    if (resp.ok || resp.status === 207) {
        // Success (200) or Multi-Status (207) for partial success
        return json;
    } else {
        // Error case - still return the JSON for processing result, but throw error
        const error: any = new Error(json.error || 'Queue submission failed');
        error.response = {
            status: resp.status,
            data: json
        };
        throw error;
    }
};

export const setFundRequestStatus = async (
    id: string,
    status: FundRequestStatus,
    note: string | null,
    formData?: {
        ap_invoice_number?: string;
        payment_type?: string;
        needs_ach_info?: boolean;
        description?: string;
        address_does_not_match?: boolean;
    }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/status`;

    const payload = { status, note, ...formData };

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(payload),
    });
    // if (!resp.ok) {
    //     throw new Error('Error updating fund request');
    // }
    const json = await resp.json();
    return json;
};

export const fetchFundRequestItemPresignedUrl = async (
    uploadPath: string,
    fileName: string
) => {
    try {
        const headers = authHeaders();
        const url = apiUrl + `${uploadPath}/presignedUrl?filename=${fileName}`;
        console.log('Requesting presigned URL from:', url);
        const resp = await fetch(url, {
            method: 'GET',
            headers,
        });

        // Check if response is ok
        if (!resp.ok) {
            console.error(`Failed to get presigned URL. Status: ${resp.status} ${resp.statusText}`);
            const errorText = await resp.text();
            console.error('Error details:', errorText);
            return { error: `Failed to get presigned URL. Status: ${resp.status}` };
        }

        const json = await resp.json();
        return json;
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to get presigned URL';
        console.error('Exception in fetchFundRequestItemPresignedUrl:', error);
        return { error: errorMessage };
    }
};

export const uploadFile = async (
    fields: Record<string, string>,
    url: string,
    file: File
) => {
    try {
        const formData = new FormData();

        // When uploading to S3, the order of fields matters
        // First add all the fields from the presigned URL
        Object.entries(fields).forEach(([k, v]) => {
            formData.append(k, v);
        });

        // The file must be the last field for S3 direct uploads
        formData.append('file', file);

        console.log(`Uploading file to URL: ${url}`);
        console.log('Fields included:', Object.keys(fields));

        const resp = await fetch(url, {
            method: 'POST',
            body: formData,
        });

        if (!resp.ok) {
            console.error(`Upload failed with status: ${resp.status} ${resp.statusText}`);
            const errorText = await resp.text();
            console.error('Error details:', errorText);
            throw new Error(`Upload failed: ${resp.status} ${resp.statusText}`);
        }

        return resp;
    } catch (error) {
        console.error('Error in uploadFile:', error);
        throw error;
    }
};

export const saveFundRequestItemImage = async (
    uploadPath: string,
    fileName: string,
    image_url: string
) => {
    const headers = authHeaders();
    const url = apiUrl + `${uploadPath}/saveUrl?filename=${fileName}`;

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ image_url }),
    });

    const json = await resp.json();
    return json;
};

export const deleteFundRequestItemUpload = async (
    uploadPath: string,
    uploadId: number
) => {
    const headers = authHeaders();
    const url = apiUrl + `${uploadPath}/${uploadId}`;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    const contentType = resp.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await resp.json() : await resp.text();
    if (!resp.ok) {
        const msg = (data && (data.error || data.message)) || 'Failed to delete upload';
        throw new Error(msg);
    }
    return data;
};

export const deleteFundRequestItemImage = async (deletePath: string) => {
    const headers = authHeaders();
    const url = apiUrl + deletePath;
    console.log('url: ', url);

    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });

    return resp;
};

export const fetchAnnualRates = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/fundrequests/annualRates';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestHistory = async (id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${id}/history`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching fund request');
    }
    const json = await resp.json();
    return json;
};

export const fetchFundRequestHistoricalExpenses = async (ntaf_id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${ntaf_id}/historical-expenses`;

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching fund request');
    }
    const json = await resp.json();
    return json;
};

export const fetchFundRequestGLCodes = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/fundrequests/glCodes';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const verifyVendor = async (
    fund_request_id: string,
    vendorId: number,
    addressDoesNotMatch?: boolean,
    confirmOverwrite?: {
        mip_data: Record<string, string>;
    }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${fund_request_id}/verify-vendor`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({
            vendor_id: vendorId,
            ...(addressDoesNotMatch !== undefined
                ? { address_does_not_match: addressDoesNotMatch }
                : {}),
            ...(confirmOverwrite
                ? {
                      confirm_overwrite: true,
                      mip_data: confirmOverwrite.mip_data,
                  }
                : {}),
        }),
    });
    const json = await resp.json();
    return json;
};

export const approveACH = async (fund_request_id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/ach-requests/${fund_request_id}/approve`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const rejectACH = async (fund_request_id: string, note: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/ach-requests/${fund_request_id}/reject`;

    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify({ note }),
    });
    const json = await resp.json();
    return json;
};

export const fetchVendorChangeRequest = async (id: string) => {
    try {
        const response = await fetch(
            `${apiUrl}/admin/vendor-change-requests/${id}`,
            {
                headers: { ...authHeaders() },
            }
        );

        const data = await response.json();
        if (response.status !== 200) {
            throw new Error(data.error || 'Failed to fetch vendor change request');
        }
        return data;
    } catch (error) {
        console.error('Error fetching vendor change request:', error);
        throw error;
    }
};

export const approveRejectVendorChangeRequest = async (
    id: string,
    action: 'approve' | 'reject',
    reason?: string
) => {
    try {
        const payload: { action: string; reason?: string } = {
            action,
        };

        if (action === 'reject' && reason) {
            payload.reason = reason;
        }

        const response = await fetch(
            `${apiUrl}/admin/vendor-change-requests/${id}`,
            {
                method: 'PUT',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }
        );

        const data = await response.json();
        if (response.status !== 200) {
            throw new Error(data.error || `Failed to ${action} vendor change request`);
        }
        return data;
    } catch (error) {
        console.error(`Error ${action}ing vendor change request:`, error);
        throw error;
    }
};

export const updateSubmittedDate = async (
    fundRequestId: string | number,
    submittedDate: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/${fundRequestId}/submitted-date`;

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ submitted_date: submittedDate }),
    });

    return handleResponse(resp);
};
